'use client';

import { ContactFormData } from '@repo/types';
import { services } from '@repo/ui/lib/api/services/index';
import { useState } from 'react';

interface ContactFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export function ContactForm({ onSuccess, onError }: ContactFormProps) {
  const [formData, setFormData] = useState<ContactFormData>({
    nom: '',
    prenom: '',
    fonction: '',
    email: '',
    groupe: '',
    objet: '',
    message: '',
    tel: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      await services.contact.createContact(formData);
      onSuccess?.();
      // Reset form
      setFormData({
        nom: '',
        prenom: '',
        fonction: '',
        email: '',
        groupe: '',
        objet: '',
        message: '',
        tel: '',
      });
    } catch (error: any) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        onError?.(error.message || 'Une erreur est survenue');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-card rounded-lg shadow-lg">
      <h2 className="text-2xl font-semibold text-foreground mb-2">Demander une démo</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Remplissez ce formulaire pour une démonstration personnalisée de Dendromap. 
        Pour vous inscrire directement, consultez nos <a href="/home#pricing" className="text-primary hover:underline font-medium">plans tarifaires</a>.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="nom" className="block text-sm font-medium text-foreground mb-2">
              Nom *
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.nom ? 'border-destructive' : 'border-border'
                }`}
              placeholder="Votre nom"
            />
            {errors.nom && <p className="mt-1 text-sm text-destructive">{errors.nom}</p>}
          </div>

          <div>
            <label htmlFor="prenom" className="block text-sm font-medium text-foreground mb-2">
              Prénom *
            </label>
            <input
              type="text"
              id="prenom"
              name="prenom"
              value={formData.prenom}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.prenom ? 'border-destructive' : 'border-border'
                }`}
              placeholder="Votre prénom"
            />
            {errors.prenom && <p className="mt-1 text-sm text-destructive">{errors.prenom}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.email ? 'border-destructive' : 'border-border'
                }`}
              placeholder="votre@email.com"
            />
            {errors.email && <p className="mt-1 text-sm text-destructive">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="tel" className="block text-sm font-medium text-foreground mb-2">
              Téléphone
            </label>
            <input
              type="tel"
              id="tel"
              name="tel"
              value={formData.tel}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.tel ? 'border-destructive' : 'border-border'
                }`}
              placeholder="Votre numéro de téléphone"
            />
            {errors.tel && <p className="mt-1 text-sm text-destructive">{errors.tel}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="fonction" className="block text-sm font-medium text-foreground mb-2">
              Fonction
            </label>
            <input
              type="text"
              id="fonction"
              name="fonction"
              value={formData.fonction}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.fonction ? 'border-destructive' : 'border-border'
                }`}
              placeholder="Votre fonction"
            />
            {errors.fonction && <p className="mt-1 text-sm text-destructive">{errors.fonction}</p>}
          </div>

          <div>
            <label htmlFor="groupe" className="block text-sm font-medium text-foreground mb-2">
              Groupe
            </label>
            <input
              type="text"
              id="groupe"
              name="groupe"
              value={formData.groupe}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.groupe ? 'border-destructive' : 'border-border'
                }`}
              placeholder="Votre groupe"
            />
            {errors.groupe && <p className="mt-1 text-sm text-destructive">{errors.groupe}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="objet" className="block text-sm font-medium text-foreground mb-2">
            Objet *
          </label>
          <input
            type="text"
            id="objet"
            name="objet"
            value={formData.objet}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.objet ? 'border-destructive' : 'border-border'
              }`}
            placeholder="Objet de votre message"
          />
          {errors.objet && <p className="mt-1 text-sm text-destructive">{errors.objet}</p>}
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows={5}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.message ? 'border-destructive' : 'border-border'
              }`}
            placeholder="Votre message..."
          />
          {errors.message && <p className="mt-1 text-sm text-destructive">{errors.message}</p>}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-primary text-white font-medium rounded-md hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
          </button>
        </div>
      </form>
    </div>
  );
}
